function valueOf(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") ?? "";
}

function clean(value) {
  return String(valueOf(value)).trim();
}

function dateFromIdNum(idNum) {
  const value = clean(idNum);
  const match = value.match(/^\d{6}(\d{4})(\d{2})(\d{2})\d{3}[\dXx]$/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
}

function idTypeFromIdNum(idNum) {
  const value = clean(idNum);
  return /^(\d{15}|\d{17}[\dXx])$/.test(value) ? "居民身份证" : "";
}

function nativePlaceFromAddress(address) {
  const value = clean(address);
  if (!value) return "";
  const municipality = value.match(/^(北京市|天津市|上海市|重庆市)/u);
  if (municipality) return municipality[1];
  const province = value.match(/^(.+?(?:省|自治区|特别行政区))/u);
  return province?.[1] || "";
}

function householdTypeFromAddress(address) {
  const value = clean(address);
  if (!value) return "";
  if (/(农村|乡|村|组|屯|生产队)/u.test(value)) return "农村户籍";
  if (/(城镇|街道|社区|居委会|小区|路|街|巷|号)/u.test(value)) return "城镇户籍";
  return "";
}

function putIfPresent(target, key, value) {
  const normalized = clean(value);
  if (normalized) target[key] = normalized;
}

export function buildTemplateVariableData({ company = {}, staff = {}, rosterEmployeeDetail = {} } = {}) {
  const variables = {};
  const idNum = valueOf(staff.idNum, staff.idNumber, staff.identityNo, rosterEmployeeDetail.idNum);
  const householdAddress = valueOf(staff.householdAddress, staff.registeredAddress, rosterEmployeeDetail.householdAddress);

  putIfPresent(variables, "companyName", valueOf(company.companyName, company.name));
  putIfPresent(variables, "companyCreditCode", valueOf(company.companyCreditCode, company.creditCode, company.unifiedSocialCreditCode));
  putIfPresent(variables, "legalRepresentative", valueOf(company.legalRepresentative, company.legalPerson));
  putIfPresent(variables, "companyPhone", valueOf(company.companyPhone, company.phone));
  putIfPresent(variables, "companyAddress", valueOf(company.companyAddress, company.address, company.registeredAddress));

  putIfPresent(variables, "staffName", valueOf(staff.staffName, staff.name));
  putIfPresent(variables, "staffCode", valueOf(staff.staffCode, staff.code));
  putIfPresent(variables, "idNum", idNum);
  putIfPresent(variables, "birthDate", valueOf(staff.birthDate, dateFromIdNum(idNum)));
  putIfPresent(variables, "idType", valueOf(staff.idType, idTypeFromIdNum(idNum)));
  putIfPresent(variables, "gender", staff.gender);
  putIfPresent(variables, "ethnicity", staff.ethnicity);
  putIfPresent(variables, "householdAddress", householdAddress);
  putIfPresent(variables, "nativePlace", valueOf(staff.nativePlace, nativePlaceFromAddress(householdAddress)));
  putIfPresent(variables, "householdType", valueOf(staff.householdType, householdTypeFromAddress(householdAddress)));
  putIfPresent(variables, "residentialAddress", valueOf(
    rosterEmployeeDetail.residentialAddress,
    rosterEmployeeDetail.currentAddress,
    staff.residentialAddress,
  ));
  putIfPresent(variables, "phone", valueOf(staff.phone, staff.mobile));
  putIfPresent(variables, "employeeAddress", valueOf(staff.employeeAddress, staff.contactAddress, staff.address));
  putIfPresent(variables, "email", staff.email);
  putIfPresent(variables, "emergencyContactName", valueOf(
    staff.emergencyContactName,
    staff.emergencyName,
    rosterEmployeeDetail.emergencyContactName,
    rosterEmployeeDetail.emergencyName,
  ));
  putIfPresent(variables, "emergencyContactRelation", valueOf(
    staff.emergencyContactRelation,
    staff.emergencyRelation,
    rosterEmployeeDetail.emergencyContactRelation,
    rosterEmployeeDetail.emergencyRelation,
  ));
  putIfPresent(variables, "emergencyContactPhone", valueOf(
    staff.emergencyContactPhone,
    staff.emergencyPhone,
    rosterEmployeeDetail.emergencyContactPhone,
    rosterEmployeeDetail.emergencyPhone,
  ));
  putIfPresent(variables, "position", valueOf(staff.position, staff.jobTitle));
  putIfPresent(variables, "onboardingDate", staff.onboardingDate);
  putIfPresent(variables, "onboardingDepartment", valueOf(staff.onboardingDepartment, staff.department));
  putIfPresent(variables, "workTypeSkillLevel", staff.workTypeSkillLevel);
  putIfPresent(variables, "workContent", staff.workContent);
  putIfPresent(variables, "workLocation", staff.workLocation);
  putIfPresent(variables, "workHourSystem", staff.workHourSystem);
  putIfPresent(variables, "workInjuryInsurance", staff.workInjuryInsurance);
  putIfPresent(variables, "salaryPlan", staff.salaryPlan);

  return variables;
}

const TEMPLATE_VARIABLE_PATTERN = /\{\{\s*([a-zA-Z][\w]*)\s*\}\}/g;

export function renderTemplateVariables(content, variables) {
  return String(content || "").replace(TEMPLATE_VARIABLE_PATTERN, (match, key) => {
    const value = variables?.[key];
    return value === undefined || value === null || String(value) === "" ? match : String(value);
  });
}

export function renderTemplateVariablesForDownload(content, variables, missingValue = "") {
  return String(content || "").replace(TEMPLATE_VARIABLE_PATTERN, (match, key) => {
    const value = variables?.[key];
    return value === undefined || value === null || String(value) === "" ? missingValue : String(value);
  });
}
